import { useState, useRef, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { supabase } from "../../lib/supabase";

interface DemoAccount {
  label: string;
  username: string;
  password: string;
  description: string;
}

const demoAccounts: DemoAccount[] = [
  {
    label: "一般會員",
    username: "demo_user01",
    password: "demo123456",
    description: "一般會員 / 體驗區認購權限",
  },
  {
    label: "VIP會員",
    username: "demo_vip01",
    password: "demo123456",
    description: "VIP會員 / 全部區域解鎖",
  },
];

function usernameToEmail(username: string): string {
  const sanitized = username.trim().toLowerCase().replace(/\s+/g, "");
  if (sanitized.includes("@")) return sanitized;
  return `${sanitized}@users.local`;
}

export default function DemoSwitcher() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const { isLoggedIn, user, signIn, signOut } = useAuth();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleQuickLogin = async (acc: DemoAccount) => {
    try {
      if (isLoggedIn) await signOut();
      const email = usernameToEmail(acc.username);
      try {
        await signIn(acc.username, acc.password);
      } catch {
        // If login fails, try to sign up first with the same email logic
        const { data, error } = await supabase.auth.signUp({
          email,
          password: acc.password,
          options: { data: { name: acc.label, username: acc.username } },
        });
        if (error) throw error;
        if (data.user) {
          await supabase.from("profiles").upsert({
            id: data.user.id,
            name: acc.label,
            username: acc.username,
            withdraw_password: "123456",
            membership_level: acc.label.includes("VIP") ? "vip" : "member",
            balance: acc.label.includes("VIP") ? 50000 : 0,
            total_earnings: acc.label.includes("VIP") ? 8500 : 0,
          });
          // Try login again
          await signIn(acc.username, acc.password);
        }
      }
      setMessage(`已以 ${acc.label} 登入`);
      setTimeout(() => setMessage(""), 2000);
      setOpen(false);
    } catch {
      setMessage("登入失敗");
      setTimeout(() => setMessage(""), 2000);
    }
  };

  const handleLogout = async () => {
    await signOut();
    setMessage("已登出");
    setTimeout(() => setMessage(""), 2000);
    setOpen(false);
  };

  return (
    <>
      {/* Floating trigger */}
      <div className="fixed bottom-4 right-4 z-[200]" ref={panelRef}>
        <button
          onClick={() => setOpen(!open)}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
            open
              ? "bg-stone-900 text-white rotate-45"
              : "bg-rose-800 text-white hover:bg-rose-900"
          }`}
          title="Demo 帳號切換"
        >
          <i className={`${open ? "ri-close-line" : "ri-flask-line"} text-lg`} />
        </button>

        {open && (
          <div className="absolute bottom-14 right-0 w-72 bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="px-4 py-3 bg-stone-900">
              <h3 className="font-display text-sm font-semibold text-white flex items-center gap-2">
                <i className="ri-flask-line text-amber-400" />
                Demo 帳號切換
              </h3>
              <p className="font-body text-[10px] text-stone-400 mt-0.5">
                快速切換不同角色權限以測試功能
              </p>
            </div>

            {/* Current status */}
            {isLoggedIn && (
              <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-100">
                <p className="font-body text-[11px] text-rose-700">
                  當前登入：{user?.user_metadata?.username || user?.email || "—"}
                </p>
              </div>
            )}

            {/* Account list */}
            <div className="p-3 flex flex-col gap-2">
              {demoAccounts.map((acc) => (
                <button
                  key={acc.username}
                  onClick={() => handleQuickLogin(acc)}
                  className="text-left p-3 rounded-xl border border-stone-200 hover:border-rose-300 hover:bg-rose-50 transition-all group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-body text-xs font-semibold text-stone-800 group-hover:text-rose-800">
                      {acc.label}
                    </span>
                    <i className="ri-login-circle-line text-stone-400 group-hover:text-rose-700 text-sm" />
                  </div>
                  <p className="font-body text-[10px] text-stone-500 mb-0.5">
                    {acc.username} / {acc.password}
                  </p>
                  <p className="font-body text-[10px] text-stone-400">
                    {acc.description}
                  </p>
                </button>
              ))}

              {isLoggedIn && (
                <button
                  onClick={handleLogout}
                  className="text-center py-2.5 rounded-xl border border-stone-200 hover:bg-stone-50 transition-all font-body text-xs text-stone-500 hover:text-rose-700"
                >
                  <i className="ri-logout-box-r-line mr-1" />
                  登出帳號
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Toast message */}
      {message && (
        <div className="fixed bottom-20 right-4 z-[201] bg-stone-900 text-white px-4 py-2.5 rounded-xl shadow-lg animate-fadeIn">
          <p className="font-body text-xs font-medium">{message}</p>
        </div>
      )}
    </>
  );
}